import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import TagTable from "./tag-table";
import TagCard from "./tag-card/tag-card";
import ApiTagManagement from "../../../api/tag-managements";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { useForm } from "antd/es/form/Form";
import { Modal } from "antd";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import useTags from "./useTags";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";

const DeleteModalComponent = ({ item, handleOk, handleCancel, isModalOpen }) => (
  <Modal
    title={item?.name}
    open={isModalOpen}
    onOk={() => handleOk(item.id, item.name)}
    okText="Delete"
    onCancel={handleCancel}
    okButtonProps={{ danger: true }}
    className="plan-page-action-modal"
  >
    <p>Are you sure you want to delete this tag?</p>
  </Modal>
);

const TagManagement = () => {
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTag, setSelectedTag] = useState({});
  const [form] = useForm();
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({ sortField: "name", sortOrder: "ASC" });

  const { tags, hasMore, isLoading, fetchNext, resetAndFetch } =
    useTags(searchValue, filters, sorting);

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } = useBulkSelection(tags, { rowKey: "id" });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const closeTagDrawer = () => {
    setSelectedTag({});
    setEditMode(false);
    setOpen(false);
  };

  const handleUpdateSearchValue = (value) => setSearchValue(value);

  const headerProps = useMemo(
    () => ({
      title: "Tag management",
      search: { value: searchValue, onHandleChange: handleUpdateSearchValue },
    }),
    [searchValue]
  );

  const updateFilterValue = (newFilters) => setFilters(newFilters);

  useAdminEvents({
    onTagChanged: (payload) => {
      resetAndFetch();

      setSelectedTag((prev) => {
        if (!prev?.id) return prev;

        const changedIds = payload?.ids || [payload?.id].filter(Boolean);
        if (!changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          closeTagDrawer();
          return {};
        }

        return payload?.tag ? { ...prev, ...payload.tag } : prev;
      });
    },
  });

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiTagManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: <p>{deletedIds.length} {plural ? "tags were" : "tag was"} deleted</p>,
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: <p>Unable to delete {failedIds.length} {plural ? "tags" : "tag"}</p>,
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      await resetAndFetch();
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete tags</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    resetAndFetch,
    clearSelection,
    setSelectedKeys,
  ]);

  const createTag = (tag) => {
    closeTagDrawer();
    ApiTagManagement.addTag(tag)
      .then(() => resetAndFetch())
      .then(() =>
        openNotification({
          type: "success",
          message: <p>{t("administration.tag.notification.created", { tag: tag.name })}</p>,
        })
      )
      .catch((e) =>
        openNotification({
          type: "error",
          message: (
            <p>{t(`administration.tag.notification.${e?.response?.data?.error?.code || "error"}`)}</p>
          ),
        })
      );
  };

  const updateTag = (id, tag) => {
    closeTagDrawer();
    ApiTagManagement.updateTag(id, tag)
      .then(() => resetAndFetch())
      .then(() =>
        openNotification({
          type: "success",
          message: <p>{t("administration.tag.notification.updated", { tag: tag.name })}</p>,
        })
      )
      .catch((e) =>
        openNotification({
          type: "error",
          message: (
            <p>{t(`administration.tag.notification.${e?.response?.data?.error?.code || "error"}`)}</p>
          ),
        })
      );
  };

  const onHandleDelete = () => setIsDeleteModalOpen(true);
  const onCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const deleteTag = (id, name) => {
    setIsDeleteModalOpen(false);
    closeTagDrawer();
    ApiTagManagement.deleteTag(id)
      .then(() => resetAndFetch())
      .then(() =>
        openNotification({
          type: "success",
          message: <p>{t("administration.tag.notification.deleted", { tag: name })}</p>,
        })
      )
      .catch((e) =>
        openNotification({
          type: "error",
          message: <p>{t(`administration.tag.notification.${e?.response?.data?.error || "error"}`)}</p>,
        })
      );
  };

  const handleSelectTag = (tag) => {
    setSelectedTag(tag);
    setOpen(true);
  };

  const toEditMode = () => setEditMode(true);

  useEffect(() => {
    form.resetFields();
  }, [selectedTag, form]);

  const sources = [
    { id: "article", name: "Article" },
    { id: "fitfact", name: "FitFact" },
  ];

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          filtersBy={[{ name: "Source", id: "sources", children: sources }]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      <TagTable
        data={tags}
        fetchData={fetchNext}
        hasMore={hasMore}
        isLoading={isLoading}
        onHandleRowClick={handleSelectTag}
        onSortingChange={setSorting}
        sorting={sorting}
        selectionColumn={getSelectionColumn()}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        }
      />

      <TagCard
        tag={selectedTag}
        isOpen={isOpen}
        close={closeTagDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createTag={createTag}
        updateTag={updateTag}
        deleteTag={onHandleDelete}
        form={form}
      />

      <DeleteModalComponent
        item={selectedTag}
        isModalOpen={isDeleteModalOpen}
        handleCancel={onCloseDeleteModal}
        handleOk={deleteTag}
      />
    </Body>
  );
};

export default TagManagement;
